import { Link } from "react-router";
import { AI_CHAT_AVAILABLE } from "../lib/feature-flags.js";

/**
 * "Ask the AI" tile. Phase 12 — global cross-data conversation surface.
 *
 * Renders 4–6 page-specific prompts. Clicking a prompt deep-links to
 * `/ai?prompt=<urlencoded>` so the /ai page pre-fills the composer.
 * Each Phase 1–11 SPA page can drop this tile in to surface common
 * questions tailored to that domain.
 *
 * Compose with `<SuggestedPromptsTile prompts={[...]} />` somewhere
 * sensible on the page (typically below the headline metrics).
 */

export interface SuggestedPromptsTileProps {
  readonly prompts: readonly string[];
  readonly title?: string;
}

export function SuggestedPromptsTile({
  prompts,
  title = "Ask the AI",
}: SuggestedPromptsTileProps) {
  if (!AI_CHAT_AVAILABLE || prompts.length === 0) return null;
  return (
    <aside
      aria-label={title}
      className="rounded-md border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-950"
    >
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
        {title}
      </h3>
      <ul className="flex flex-col gap-1">
        {prompts.map((p) => (
          <li key={p}>
            <Link
              to={`/ai?prompt=${encodeURIComponent(p)}`}
              className="block rounded-md px-2 py-1 text-sm text-brand-700 underline decoration-brand-500/40 underline-offset-2 transition hover:bg-brand-50 hover:decoration-brand-600 dark:text-brand-300 dark:hover:bg-brand-950/30"
            >
              {p}
            </Link>
          </li>
        ))}
      </ul>
    </aside>
  );
}
